import type { FeatureModule, FeatureState } from '../types';

export default function createReadingMaskModule(): FeatureModule {
  let enabled = false;
  let maskTop: HTMLElement | null = null;
  let maskBottom: HTMLElement | null = null;
  let viewportHeight = 120;

  const STORAGE_KEY = 'accessify-reading-mask';

  function createOverlays() {
    maskTop = document.createElement('div');
    maskBottom = document.createElement('div');

    // R15-2 Fix: individual style-Setter statt setAttribute('style', …) —
    // setAttribute löst sonst nonce-/strict-CSP-Violations aus.
    const applyBase = (el: HTMLElement) => {
      el.style.position = 'fixed';
      el.style.left = '0';
      el.style.right = '0';
      el.style.zIndex = '999990';
      el.style.background = 'rgba(0, 0, 0, 0.65)';
      el.style.pointerEvents = 'none';
      el.style.transition = 'top 80ms ease, bottom 80ms ease, height 80ms ease';
    };
    applyBase(maskTop);
    maskTop.style.top = '0';
    maskTop.setAttribute('aria-hidden', 'true');
    maskTop.id = 'accessify-mask-top';

    applyBase(maskBottom);
    maskBottom.style.bottom = '0';
    maskBottom.setAttribute('aria-hidden', 'true');
    maskBottom.id = 'accessify-mask-bottom';

    // An documentElement statt body (wie reading-guide/magnifier/TTS-Leiste):
    // page-zoom setzt `body { zoom }`, body-Kinder skalieren mit und die aus
    // unskalierten Viewport-Koordinaten (clientY/innerHeight) berechneten
    // Maskenhöhen säßen um den Zoom-Faktor daneben.
    document.documentElement.appendChild(maskTop);
    document.documentElement.appendChild(maskBottom);
  }

  function updateMask(y: number) {
    if (!maskTop || !maskBottom) return;
    const half = viewportHeight / 2;
    const topH = Math.max(0, y - half);
    const bottomH = Math.max(0, window.innerHeight - y - half);
    maskTop.style.height = topH + 'px';
    maskBottom.style.height = bottomH + 'px';
  }

  function handleMouseMove(e: MouseEvent) {
    updateMask(e.clientY);
  }

  // Touch: don't follow the finger continuously — that fought the page scroll
  // and the focus window jumped around. Let the page scroll normally and
  // reposition the focus window only on a deliberate TAP. A touch that moves
  // past a small threshold is a scroll, not a tap.
  let touchStartX = 0;
  let touchStartY = 0;
  let touchMoved = false;
  function handleTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) { touchMoved = true; return; }
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchMoved = false;
  }
  function handleTouchMove(e: TouchEvent) {
    if (e.touches.length !== 1) return;
    if (Math.abs(e.touches[0].clientX - touchStartX) > 10 || Math.abs(e.touches[0].clientY - touchStartY) > 10) {
      touchMoved = true;
    }
  }
  function handleTouchEnd(e: TouchEvent) {
    if (touchMoved || !enabled) return;
    const t = e.changedTouches[0];
    if (!t) return;
    updateMask(t.clientY);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (!maskTop || !maskBottom) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const active = document.activeElement as HTMLElement | null;
      if (!active) return;
      const rect = active.getBoundingClientRect();
      const y = rect.top + rect.height / 2;
      const half = viewportHeight / 2;
      maskTop.style.height = Math.max(0, y - half) + 'px';
      maskBottom.style.height = Math.max(0, window.innerHeight - y - half) + 'px';
    }
  }

  function activate() {
    enabled = true;
    createOverlays();
    // Show the focus window immediately, centered. Previously the mask stayed
    // invisible until the first mousemove — so on touch (no mousemove) it never
    // appeared at all.
    updateMask(window.innerHeight / 2);
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
  }

  function deactivate() {
    enabled = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('keydown', handleKeyDown);
    maskTop?.remove();
    maskBottom?.remove();
    maskTop = null;
    maskBottom = null;
  }

  return {
    id: 'reading-mask',
    name: () => 'Reading Mask',
    description: 'Focus window that dims surrounding content',
    icon: 'reading-mask',
    category: 'motor',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'reading-mask', enabled, value: viewportHeight }),
    setState: (height: number) => {
      viewportHeight = Math.min(400, Math.max(60, height));
    },
  };
}
